import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

// Module-level flag to prevent multiple subscriptions from different hook instances
// This is critical because ProductCard uses this hook and there can be many cards
let vendorProductSubscriptionActive = false;

/**
 * Hook for vendor product management - includes ALL products (even 0 quantity)
 * This is different from useMyProducts which only shows products available for sale
 */
export function useVendorProducts() {
  const queryClient = useQueryClient();
  const isSubscriptionOwner = useRef(false);

  // Set up real-time subscription for product changes - ONLY ONCE globally
  useEffect(() => {
    // Skip if subscription is already active from another hook instance
    if (vendorProductSubscriptionActive) {
      return;
    }
    
    vendorProductSubscriptionActive = true;
    isSubscriptionOwner.current = true;
    
    const channel = supabase
      .channel('vendor-product-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'products'
        },
        (payload) => {
          console.log('Vendor product changed:', payload);
          // Invalidate queries when products change
          queryClient.invalidateQueries({ queryKey: ['vendor-products'] });
          queryClient.invalidateQueries({ queryKey: ['my-products'] });
        }
      )
      .subscribe();

    return () => {
      // Only clean up if this instance owns the subscription
      if (isSubscriptionOwner.current) {
        vendorProductSubscriptionActive = false;
        supabase.removeChannel(channel);
      }
    };
  }, [queryClient]);

  const query = useQuery({
    queryKey: ['vendor-products'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];

      console.log('Fetching vendor products for user:', session.user.id);

      // Get active vendors that are not deleted
      const { data: vendors, error: vendorsError } = await supabase
        .from('vendors')
        .select('id, store_name')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .not('is_deleted', 'eq', true);

      if (vendorsError) {
        console.error('Error fetching vendors:', vendorsError);
        throw vendorsError;
      }

      if (!vendors || vendors.length === 0) {
        console.log('No vendors found for user');
        return [];
      }

      console.log(`Found ${vendors.length} vendors for user`);
      
      const vendorIds = vendors.map(vendor => vendor.id);
      
      console.log('Getting all vendor products for vendor IDs:', vendorIds);

      // Fetch ALL products for vendor management (including quantity 0)
      const { data: allProducts, error: productsError } = await supabase
        .from('products')
        .select('*')
        .in('vendor_id', vendorIds)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Error fetching vendor products:', productsError);
        throw productsError;
      }
      
      console.log('Found vendor products count:', allProducts?.length || 0);
      return allProducts || [];
    },
    staleTime: 60000, // 60 seconds - reduces refetch frequency significantly
    refetchOnMount: false, // Don't refetch on every mount - CRITICAL for performance
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase
        .from('products')
        .update({ status: 'deleted' })
        .eq('id', productId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all product-related queries to refresh all screens
      queryClient.invalidateQueries({ queryKey: ['vendor-products'] });
      queryClient.invalidateQueries({ queryKey: ['my-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  const updateProductQuantity = useMutation({
    mutationFn: async ({ productId, quantity }: { productId: string; quantity: number }) => {
      const { error } = await supabase
        .from('products')
        .update({ quantity })
        .eq('id', productId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate both vendor and sell products queries
      queryClient.invalidateQueries({ queryKey: ['vendor-products'] });
      queryClient.invalidateQueries({ queryKey: ['my-products'] });
    },
  });

  return {
    ...query,
    deleteProduct: deleteProduct.mutate,
    updateProductQuantity: updateProductQuantity.mutate,
  };
}